import { getStoreInfo } from './api';

/**
 * v5 — MODULES STORE : activation/désactivation par l'admin Restomi.
 *
 * Permet à l'admin de couper le kiosk d'un restaurant (impayé, suspension...).
 * Le backend refuse déjà les commandes, mais le kiosk vérifie au démarrage pour
 * éviter que le client compose tout son panier avant de se prendre un refus au paiement.
 *
 * ⚠️ NE PAS CONFONDRE avec le mode RUSH :
 *  - module désactivé = blocage ADMIN (abonnement) → "Contactez Restomi"
 *  - rush_active      = pause temporaire du restaurateur → "Restaurant surchargé"
 * Deux mécanismes distincts, deux messages différents.
 *
 * ⚠️ FAIL-OPEN : si l'appel échoue (réseau, timeout), on NE bloque PAS.
 * Mieux vaut laisser passer une commande que d'immobiliser un resto sur un bug réseau.
 * (Même logique que le backend.)
 */

// ⚠️ Cet endpoint n'est PAS sous /v3/api/ comme les autres, mais bien /v3/ (spec backend).
const MODULES_URL = 'https://api.restomi.fr/v3/get_store_modules';

export interface StoreModules {
  success: boolean;
  store_id?: string;
  is_kiosk_enabled: boolean;
  is_web_enabled: boolean;
  is_caisse_enabled: boolean;
  is_qr_reward_enabled: boolean;
}

/** Fallback fail-open : tout est activé (on ne bloque jamais sur erreur technique) */
export const MODULES_FAIL_OPEN: StoreModules = {
  success: false,
  is_kiosk_enabled: true,
  is_web_enabled: true,
  is_caisse_enabled: true,
  is_qr_reward_enabled: true,
};

/**
 * Récupère l'état des modules du store.
 *
 * @returns null en cas d'erreur → l'appelant doit appliquer le fail-open.
 */
export async function fetchStoreModules(): Promise<StoreModules | null> {
  try {
    const storeInfo = getStoreInfo();
    if (!storeInfo?.store_id) {
      console.warn('[Modules] store_id non disponible');
      return null;
    }
    const response = await fetch(MODULES_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // ⚠️ header "lang" OBLIGATOIRE, sinon le backend renvoie HTTP 500
        lang: '0',
      },
      body: JSON.stringify({ store_id: storeInfo.store_id }),
    });
    if (!response.ok) {
      console.warn('[Modules] get_store_modules HTTP', response.status);
      return null;
    }
    const data = await response.json();
    if (!data || data.success !== true) {
      console.warn('[Modules] get_store_modules success=false', data);
      return null;
    }
    // Fail-open champ par champ : un champ absent ne doit jamais bloquer.
    const modules: StoreModules = {
      success: true,
      store_id: data.store_id,
      is_kiosk_enabled: data.is_kiosk_enabled !== false,
      is_web_enabled: data.is_web_enabled !== false,
      is_caisse_enabled: data.is_caisse_enabled !== false,
      is_qr_reward_enabled: data.is_qr_reward_enabled !== false,
    };
    console.log('[Modules] get_store_modules ←', modules);
    return modules;
  } catch (e) {
    console.error('[Modules] fetchStoreModules error', e);
    return null;
  }
}

/**
 * Détecte un refus de commande pour module désactivé.
 * Backend : { success: false, reason: "module_disabled", module: "kiosk",
 *             error_description: "Module Kiosk desactive pour ce restaurant. Contactez Restomi." }
 */
export function parseModuleDisabled(
  response: any
): { module: string; description: string } | null {
  if (!response || response.success !== false) return null;
  if (response.reason !== 'module_disabled') return null;
  return {
    module: response.module || 'kiosk',
    description: response.error_description || '',
  };
}
